// axios
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

// project imports
import { DATA_SOURCES } from '../constants/appDataSources';
import { applyAuthToConfig } from 'services/auth/authTransport';
import { handleRevokedSession, refreshAccessToken } from 'services/auth/sessionRefresh';
import { getApiBaseUrl } from 'services/config/runtimeConfig';

interface AzureFunctionRequestConfig extends AxiosRequestConfig {
  appDataSourceId?: string;
  accessToken?: string;
}

const getAccessTokenFromUrl = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('accessToken');
  }
  return null;
};

/**
 * Makes a request to a protected azure function that matches the specified url.
 * @param {AxiosRequestConfig} requestConfig Axios request config. Only the `url` is required. For all available options see https://axios-http.com/docs/req_config.
 * @returns {Promise<AxiosResponse<T>>} Axios response.
 */
const callAzureFunction = async <T = unknown,>(requestConfig: AzureFunctionRequestConfig): Promise<AxiosResponse<T>> => {
  const tokenFromUrl = getAccessTokenFromUrl();

  // If we have an accessToken, add it to the query params
  let params = requestConfig.params || {};
  if (tokenFromUrl) {
    params = { ...params, accessToken: tokenFromUrl };
  } else {
    await refreshAccessToken();
  }

  // Destructure appDataSourceId and other params from requestConfig
  const { appDataSourceId, ...axiosConfig } = requestConfig;

  // Add appDataSourceId to params if it is provided and not 'default'
  const updatedParams = {
    ...params,
    ...(appDataSourceId && appDataSourceId !== DATA_SOURCES.default.id ? { appDataSourceId } : {})
  };

  try {
    return await axios(
      applyAuthToConfig({
        ...axiosConfig,
        params: updatedParams, // use updated params with optional appDataSourceId
        baseURL: getApiBaseUrl()
      })
    );
  } catch (error) {
    await handleRevokedSession(error);
    throw error;
  }
};

export default callAzureFunction;
